import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const DataPortabilityForm = () => {
  const [selectedData, setSelectedData] = useState({
    profile: true,
    notes: true,
    groups: true,
    settings: true,
    activityLog: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleDataTypeChange = (dataType: keyof typeof selectedData, checked: boolean) => {
    setSelectedData(prev => ({
      ...prev,
      [dataType]: checked
    }));
  };

  const collectUserData = async (userId: string) => {
    const exportData: any = {
      exportDate: new Date().toISOString(),
      userId: userId,
      data: {}
    };

    try {
      // Collect profile data if selected
      if (selectedData.profile) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();
        
        if (profile) {
          exportData.data.profile = {
            id: profile.id,
            username: profile.username,
            avatar_url: profile.avatar_url,
            subscription_status: profile.subscription_status,
            subscription_type: profile.subscription_type,
            trial_end_date: profile.trial_end_date,
            created_at: profile.created_at,
            updated_at: profile.updated_at
          };
        }
      }

      // Collect notes data if selected
      if (selectedData.notes) {
        const { data: notes } = await supabase
          .from('notes')
          .select('*')
          .eq('user_id', userId);
        
        if (notes) {
          exportData.data.notes = notes.map(note => ({
            id: note.id,
            title: note.title,
            content: note.content,
            color: note.color,
            group_id: note.group_id,
            cover_image: note.cover_image,
            is_public: note.is_public,
            has_public_share: note.has_public_share,
            has_private_share: note.has_private_share,
            share_id: note.share_id,
            shared_at: note.shared_at,
            shared_by: note.shared_by,
            collaborative_editing: note.collaborative_editing,
            created_at: note.created_at,
            updated_at: note.updated_at
          }));
        }
      }

      // Collect groups data if selected
      if (selectedData.groups) {
        const { data: groups } = await supabase
          .from('groups')
          .select('*')
          .eq('user_id', userId);
        
        if (groups) {
          exportData.data.groups = groups.map(group => ({
            id: group.id,
            name: group.name,
            created_at: group.created_at
          }));
        }
      }

      // Collect settings/preferences if selected (using support_tickets as a proxy for user activity)
      if (selectedData.settings) {
        const { data: supportTickets } = await supabase
          .from('support_tickets')
          .select('*')
          .eq('user_id', userId);
        
        if (supportTickets && supportTickets.length > 0) {
          exportData.data.settings = {
            support_history: supportTickets.map(ticket => ({
              id: ticket.id,
              title: ticket.title,
              created_at: ticket.created_at,
              satisfaction_rating: ticket.satisfaction_rating
            }))
          };
        }
      }

      // Collect activity log if selected
      if (selectedData.activityLog) {
        const { data: invitations } = await supabase
          .from('email_invitations')
          .select('*')
          .eq('user_id', userId);
        
        const { data: referralLinks } = await supabase
          .from('referral_links')
          .select('*')
          .eq('user_id', userId);
        
        exportData.data.activityLog = {
          email_invitations: invitations?.map(inv => ({
            invited_email: inv.invited_email,
            status: inv.status,
            sent_at: inv.sent_at
          })) || [],
          referral_links: referralLinks?.map(link => ({
            code: link.code,
            successful_referrals: link.successful_referrals,
            reward_claimed: link.reward_claimed,
            created_at: link.created_at
          })) || []
        };
      }

      return exportData;
    } catch (error) {
      console.error('Error collecting user data:', error);
      throw error;
    }
  };

  const downloadData = (data: any, filename: string) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to export your data.",
          variant: "destructive",
        });
        return;
      }

      // Collect actual user data
      const exportData = await collectUserData(user.id);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `mindnote-data-export-${timestamp}.json`;
      
      // Download the data
      downloadData(exportData, filename);

      // Log the export request for compliance
      console.log('Data export completed for user:', user.id);
      console.log('Export includes:', Object.keys(exportData.data));

      toast({
        title: "Data export completed",
        description: `Your data has been exported and downloaded as ${filename}. The file contains all selected data in JSON format.`,
        duration: 6000,
      });

      // Reset form
      setSelectedData({
        profile: true,
        notes: true,
        groups: true,
        settings: true,
        activityLog: false,
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "Export Error",
        description: "Failed to export your data. Please try again or contact support at hello@mindnote.online.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Data Portability Request</CardTitle>
        <CardDescription>
          Request a copy of your personal data. Under GDPR and CCPA, you have the right to receive your data in a portable format.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Select the data you want to export:</h4>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="profile"
                checked={selectedData.profile}
                onCheckedChange={(checked) => handleDataTypeChange('profile', checked as boolean)}
              />
              <label htmlFor="profile" className="text-sm">
                Profile Information (username, avatar, subscription)
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="notes"
                checked={selectedData.notes}
                onCheckedChange={(checked) => handleDataTypeChange('notes', checked as boolean)}
              />
              <label htmlFor="notes" className="text-sm">
                Notes and Content
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="groups"
                checked={selectedData.groups}
                onCheckedChange={(checked) => handleDataTypeChange('groups', checked as boolean)}
              />
              <label htmlFor="groups" className="text-sm">
                Groups and Organization
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="settings"
                checked={selectedData.settings}
                onCheckedChange={(checked) => handleDataTypeChange('settings', checked as boolean)}
              />
              <label htmlFor="settings" className="text-sm">
                Account Settings and Support History
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="activityLog"
                checked={selectedData.activityLog}
                onCheckedChange={(checked) => handleDataTypeChange('activityLog', checked as boolean)}
              />
              <label htmlFor="activityLog" className="text-sm">
                Activity Log and Usage Data
              </label>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h5 className="text-sm font-medium text-green-900 mb-2">💾 Direct Download</h5>
          <p className="text-xs text-green-800">
            Your data will be immediately downloaded as a JSON file when you click the export button below.
            The file will contain all selected data in a machine-readable format.
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h5 className="text-sm font-medium text-blue-900 mb-2">What will be exported?</h5>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• All data will be provided in JSON format for easy parsing</li>
            <li>• Personal information will be included as selected above</li>
            <li>• The export includes creation and modification timestamps</li>
            <li>• No sensitive authentication data will be included</li>
          </ul>
        </div>

        <Button 
          onClick={handleSubmit} 
          disabled={isSubmitting || !Object.values(selectedData).some(Boolean)}
          className="w-full"
        >
          {isSubmitting ? "Collecting & Exporting Data..." : "Export & Download My Data"}
        </Button>

        <div className="text-xs text-gray-500">
          <p>
            This request is processed in accordance with Article 20 of the GDPR and Section 1798.100 of the CCPA.
            If you have any questions about this process, please contact our privacy team at hello@mindnote.online.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default DataPortabilityForm;
